import { ReactNode, useEffect, useState } from 'react'
import { Avatar, Box, styled, Tooltip } from '@mui/material'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { matchPath, PathPattern } from 'react-router-dom'
import { AppBarNavLink } from 'ComponentLibrary/src/molecules/AppBar/AppBarNavLink'
import { CrownSimpleIcon } from '@phosphor-icons/react'

type BaseNavigationItemProps = {
    currentPath: string
    size?: 'small' | 'medium'
    icon?: ReactNode
    caps?: boolean
    label?: string
    tooltipLabel?: string
    pathPatterns?: PathPattern['path'][]
}

type NavigationLinkItemProps = BaseNavigationItemProps & {
    path: string
    onClick?: never
}

type NavigationActionItemProps = BaseNavigationItemProps & {
    path?: never
    onClick: () => void
}

export type NavigationItemProps = NavigationLinkItemProps | NavigationActionItemProps
type AvatarNavigationItemProps = NavigationLinkItemProps & {
    imageUrl?: string
    imageThumbnailUrl?: string
    fallbackLabel?: string
    isPlatformUser?: boolean
    overlayIcon?: ReactNode
}

function isNavigationLinkItem(props: NavigationItemProps): props is NavigationLinkItemProps {
    return typeof props.path === 'string'
}

export const NavigationItem = (props: NavigationItemProps) => {
    const { currentPath, label, tooltipLabel, icon, pathPatterns, caps = true, size = 'small' } = props
    const isSelected = isNavigationLinkItem(props) ? isNavItemSelected({ currentPath, path: props.path, pathPatterns }) : false
    const showLabel = size === 'medium' || isSelected

    return (
        <Tooltip title={!showLabel ? (tooltipLabel ?? label) : ''}>
            <Box display="flex" alignItems="center">
                {isNavigationLinkItem(props) ? (
                    <AppBarNavLink
                        caps={caps}
                        href={props.path}
                        label={showLabel ? label : undefined}
                        startIcon={icon}
                        selected={isSelected}
                    />
                ) : (
                    <AppBarNavLink
                        caps={caps}
                        onClick={props.onClick}
                        label={showLabel ? label : undefined}
                        startIcon={icon}
                        selected={isSelected}
                    />
                )}
            </Box>
        </Tooltip>
    )
}

export const AvatarNavigationItem = ({
    path,
    currentPath,
    label,
    tooltipLabel,
    icon,
    fallbackLabel,
    size = 'small',
    imageUrl,
    imageThumbnailUrl,
    pathPatterns,
    isPlatformUser,
    overlayIcon,
}: AvatarNavigationItemProps) => {
    const isSelected = isNavItemSelected({ currentPath, path, pathPatterns })
    const showLabel = size === 'medium' || isSelected
    const [avatarImageSrc, setAvatarImageSrc] = useState(imageThumbnailUrl)
    const tooltipTitle = tooltipLabel ?? fallbackLabel ?? ''
    const isIconOnly = !label
    const showPlatformBadge = showLabel && !isIconOnly && !!isPlatformUser

    useEffect(() => {
        setAvatarImageSrc(imageThumbnailUrl)
    }, [imageThumbnailUrl])

    const AvatarIcon = avatarImageSrc ? (
        <Avatar
            src={avatarImageSrc}
            slotProps={{
                img: {
                    onError: () => {
                        if (imageUrl && imageUrl !== avatarImageSrc) {
                            setAvatarImageSrc(imageUrl)
                        }
                    },
                },
            }}
            sx={{
                width: tokens.SizingIconM,
                height: tokens.SizingIconM,
            }}
        />
    ) : (
        icon
    )

    return (
        <Tooltip title={!showLabel || isIconOnly ? tooltipTitle : ''}>
            <Box display="flex" alignItems="center">
                <AppBarNavLink
                    caps={false}
                    href={path}
                    label={showLabel ? label : undefined}
                    startIcon={
                        <AvatarIconContainer data-app-bar-avatar-icon $selected={isSelected}>
                            {AvatarIcon}
                        </AvatarIconContainer>
                    }
                    endIcon={showPlatformBadge ? <CrownSimpleIcon size={tokens.SizingIconS} weight="bold" /> : undefined}
                    overlay={overlayIcon}
                    selected={isSelected}
                />
            </Box>
        </Tooltip>
    )
}

const AvatarIconContainer = styled('span', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected: boolean }>(({ $selected }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: tokens.SizingIconM,
    height: tokens.SizingIconM,
    borderRadius: '50%',
    outline: $selected ? `${tokens.SizingBorderM} solid ${tokens.ColorBorderSelectedDefault}` : undefined,
    overflow: 'hidden',
    flexShrink: 0,
    '& > *': {
        width: '100%',
        height: '100%',
    },
}))

/**
 * An optional array of path patterns to determine whether this navigation item should be active.
 * Each pattern should be a valid React Router path pattern.
 * If any of the patterns matches the current path (using `matchPath` with `end: true`), the item is considered active.
 *
 * This is useful when paths are dynamic and nested.
 *
 * Example:
 *   pathPatterns={['/customers/:id/orders/:id', '/orders']}
 */
export function isNavItemSelected({
    path,
    pathPatterns,
    currentPath,
}: {
    pathPatterns: PathPattern['path'][] | undefined
    path: string
    currentPath: string
}): boolean {
    return pathPatterns !== undefined
        ? pathPatterns.some((pattern) => matchPath({ path: pattern, end: true }, currentPath))
        : !!matchPath({ path, end: false }, currentPath)
}
